/**
 * 頁 14 — 週報／匯出（`ui-spec` §2 頁 14；P1-12）
 *
 * 週報（倉租明細 ＋ 出入庫摘要 ＋ 入庫批次 ＋ 未完事項）＋ **Excel（`.xlsx`）／CSV 匯出**
 * ＋「上一週／下一週／揀日期」。
 *
 * 紅線
 *   · ⭐ `rules §七 F1`：數字一律由 `GET /api/reports/weekly` 出（同週費頁、帳本**同一支實作**）
 *     —— 前端零計算（只做顯示格式）
 *   · ⭐ `F2`：匯出檔一律經 `@coeldery/table-io`（`lib/report-export.ts`），唔准自己砌 XML／CSV
 *   · 權限：**只准職員／管理員**（後端 `guard()` 403；前端亦唔顯示入口）
 *   · `weekStart` 任意日期 → 後端歸該週**結算日**（`settings.fee.settlement_weekday`；`decisions/013`／`019` 起逢三）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { btnGhost, btnPrimary, errText, hint as hintStyle, input } from '@coeldery/ui-kit';
import ReportTables from '../components/ReportTables';
import { ApiError, api, type WeeklyReport } from '../lib/api';
import { reportFile, downloadReport, type ReportFormat } from '../lib/report-export';
import type { ReportLabels } from '../lib/report-sheets';

/** 週位移（`YYYY-MM-DD`；用 UTC 避開時區令日期飄移） */
function shiftWeek(iso: string, weeks: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

export default function Reports() {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState('');
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(
    async (week: string) => {
      setError(null);
      try {
        setReport(await api.weeklyReport(week || undefined));
      } catch (err) {
        setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    void load(weekStart);
  }, [load, weekStart]);

  /** 匯出表頭（**一律行 i18n**；`report-sheets.ts` 唔准寫死文字） */
  const labels: ReportLabels = {
    sheetFee: t('report.sheetFee'),
    sheetLedger: t('report.sheetLedger'),
    sheetBatches: t('report.sheetBatches'),
    sheetPending: t('report.sheetPending'),
    week: t('report.week'),
    openingCbm: t('weeklyFee.openingCbm'),
    outboundCbm: t('weeklyFee.outboundCbm'),
    closingCbm: t('weeklyFee.closingCbm'),
    storageRate: t('weeklyFee.storageRate'),
    storageAmount: t('weeklyFee.storageAmount'),
    handlingAmount: t('weeklyFee.handlingAmount'),
    totalAmount: t('weeklyFee.totalAmount'),
    status: t('report.status'),
    type: t('report.type'),
    count: t('report.count'),
    cartons: t('report.cartons'),
    units: t('report.units'),
    cbm: t('report.cbm'),
    batchNo: t('report.batchNo'),
    supplier: t('report.supplier'),
    receivedDate: t('report.receivedDate'),
    receivedCbm: t('report.receivedCbm'),
    handlingFee: t('report.handlingFee'),
    item: t('report.item'),
    value: t('report.value'),
  };

  const exportAs = (format: ReportFormat) => {
    if (!report) return;
    setNotice(null);
    setError(null);
    try {
      const pendingRows: Array<[string, number]> = [
        [t('pending.unsettledTitle'), report.pending.unsettledEvents],
        [t('pending.missingChopTitle'), report.pending.missingChop],
        [t('pending.onSiteTitle'), report.pending.onSiteCbm],
      ];
      const file = reportFile(report, labels, format, pendingRows);
      downloadReport(file);
      setNotice(t('report.exported', { name: file.filename }));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.loadFailed'));
    }
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('report.title')}</h2>
      <p style={hintStyle}>{t('report.intro')}</p>

      {/* 揀週（上一週／日期／下一週）；後端會歸該週星期一 */}
      <section style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', marginTop: '10px' }}>
        <button
          type="button"
          style={btnGhost}
          onClick={() => report && setWeekStart(shiftWeek(report.weekStart, -1))}
          disabled={!report}
        >
          {t('report.prevWeek')}
        </button>
        <input
          type="date"
          style={{ ...input, width: 'auto' }}
          value={report?.weekStart ?? ''}
          onChange={(e) => setWeekStart(e.target.value)}
          aria-label={t('report.week')}
        />
        <button
          type="button"
          style={btnGhost}
          onClick={() => report && setWeekStart(shiftWeek(report.weekStart, 1))}
          disabled={!report}
        >
          {t('report.nextWeek')}
        </button>
        <button type="button" style={btnGhost} onClick={() => setWeekStart('')}>
          {t('report.thisWeek')}
        </button>
        {report && (
          <button type="button" style={btnPrimary} onClick={() => exportAs('xlsx')}>
            {t('report.exportXlsx')}
          </button>
        )}
        {report && (
          <button type="button" style={btnGhost} onClick={() => exportAs('csv')}>
            {t('report.exportCsv')}
          </button>
        )}
      </section>

      {error && (
        <p style={errText} role="alert">
          {error}
        </p>
      )}
      {notice && <p style={{ ...hintStyle, fontWeight: 700 }}>{notice}</p>}

      {report && report.issues.length > 0 && (
        <p style={errText} role="alert">
          {t('report.issues')}
          {report.issues.join('；')}
        </p>
      )}

      {report && <ReportTables report={report} />}

      {report && (
        <p style={{ ...hintStyle, marginTop: '12px' }}>
          {t('report.pendingLine', {
            events: report.pending.unsettledEvents,
            chop: report.pending.missingChop,
          })}
          {'　'}
          <Link to="/pending" style={{ fontWeight: 700 }}>
            {t('dash.seeAllPending')}
          </Link>
        </p>
      )}

      <p style={{ ...hintStyle, marginTop: '12px' }}>
        <Link to="/weekly-fees" style={{ fontWeight: 700 }}>
          {t('report.gotoFees')}
        </Link>
      </p>
    </>
  );
}
